import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";
import { useCompanyMutation } from "@/hooks/useCompanyMutation";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/AuthProvider";
import { logAuditAction } from "@/lib/audit";

export function useFinancialData(activeCompanyId: string | null) {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const today = new Date();

  const { data: openRegister, isLoading: loadingRegister } = useQuery({
    queryKey: ["open_register", activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_cash_registers")
        .select("*")
        .eq("company_id", activeCompanyId)
        .eq("status", "OPEN")
        .order("opened_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data || null;
    },
    enabled: !!activeCompanyId,
  });

  const { data: transactions = [], isLoading: loadingTx } = useQuery({
    queryKey: ["cash_transactions", activeCompanyId, openRegister?.id],
    queryFn: async () => {
      let query = supabase
        .from("cash_transactions")
        .select("*")
        .eq("company_id", activeCompanyId);

      if (openRegister) {
        query = query.eq("register_id", openRegister.id);
      } else {
        const start = startOfDay(today).toISOString();
        const end = endOfDay(today).toISOString();
        query = query.gte("created_at", start).lte("created_at", end);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId,
  });

  const openCashRegister = useMutation({
    mutationFn: async (initialBal: number) => {
      const { data, error } = await supabase
        .from("tenant_cash_registers")
        .insert({
          company_id: activeCompanyId,
          opened_by: userId,
          initial_balance: initialBal,
          status: "OPEN",
        })
        .select()
        .single();
      if (error) throw error;

      await logAuditAction({
        companyId: activeCompanyId!,
        type: "CASH_REGISTER_OPEN",
        details: `Caixa aberto com R$ ${initialBal.toFixed(2)}`,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["open_register"] });
    },
  });

  const closeCashRegister = useMutation({
    mutationFn: async (payload: any) => {
      const { error } = await supabase
        .from("tenant_cash_registers")
        .update({
          status: "CLOSED",
          closed_at: new Date().toISOString(),
          closed_by: userId,
          expected_amount: payload.expectedTotal,
          counted_amount: payload.countedTotal,
          difference: payload.diff,
          notes: payload.notes,
        })
        .eq("id", openRegister.id);

      if (error) throw error;

      await logAuditAction({
        companyId: activeCompanyId!,
        type: "CASH_REGISTER_CLOSE",
        details: `Caixa fechado. Diferença: R$ ${payload.diff.toFixed(2)}. Observações: ${payload.notes}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["open_register"] });
    },
  });

  const registerExpense = useCompanyMutation({
    mutationFn: async ({
      description,
      amount,
      category,
      method,
    }: {
      description: string;
      amount: string;
      category: string;
      method: string;
    }) => {
      if (!description || !amount)
        throw new Error("Preencha os campos obrigatórios");

      const numericAmount = Number(amount);
      if (Number.isNaN(numericAmount) || numericAmount <= 0)
        throw new Error("Valor inválido");

      const { error } = await supabase.from("cash_transactions").insert({
        id: crypto.randomUUID(),
        company_id: activeCompanyId,
        register_id: openRegister?.id || null,
        type: "SAIDA",
        amount: numericAmount,
        method: method,
        category: category,
        description: description,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial_transactions"] });
    },
  });

  return {
    openRegister,
    transactions,
    loading: loadingTx || loadingRegister,
    openCashRegister,
    closeCashRegister,
    registerExpense,
  };
}
